import { TbHelpCircle } from "solid-icons/tb";
import { type Component, For, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { MODEL_VENDORS } from "~/utils/modelVendors";
import { useModelSelectorLogic } from "../hooks/useModelSelectorLogic";

/**
 * Model selector section component for the settings dropdown
 * Optimized for space efficiency within the custom tooltip
 */
export const ModelSelectorSection: Component = () => {
  const { currentModel, currentVendor, currentModelName, selectModel, isModelSelected, modelGroups } =
    useModelSelectorLogic();

  return (
    <div class="">
      {/* Current Model Display */}
      <div class="flex items-center justify-between mb-3">
        <span class="text-sm font-medium text-primary-text">Model Selection</span>
        <div class="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted/10 border border-muted-border">
          <Dynamic component={currentVendor().icon} size={14} class={currentVendor().color} />
          <span class="text-xs font-medium text-primary-text">{currentModelName()}</span>
        </div>
      </div>

      {/* Model List */}
      <div class="max-h-[17.5rem] overflow-y-auto space-y-1 mb-2">
        <For each={modelGroups}>
          {(group, groupIndex) => {
            const vendor = MODEL_VENDORS[group.vendor];
            const GroupIcon = vendor.icon;

            return (
              <>
                <Show when={groupIndex() > 0}>
                  <div class="h-px bg-primary-border my-1" />
                </Show>

                {/* Vendor Header */}
                <div class="px-2 py-1">
                  <div class="flex items-center gap-1.5">
                    <GroupIcon size={12} class={vendor.color} />
                    <span class="text-xs font-medium text-muted-text">{vendor.name}</span>
                  </div>
                </div>

                {/* Model Options */}
                <For each={group.models}>
                  {(model) => {
                    const modelValue = model.value;
                    const isSelected = () => isModelSelected(modelValue);

                    return (
                      <button
                        type="button"
                        onClick={() => selectModel(modelValue)}
                        class={`w-full flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer text-left transition-colors ${
                          isSelected()
                            ? "bg-primary dark:bg-dark-primary text-on-primary dark:text-dark-on-primary"
                            : "hover:bg-inset"
                        }`}
                        aria-pressed={isSelected()}
                      >
                        {/* Radio button indicator */}
                        <div
                          class={`w-3 h-3 rounded-full border border-primary-border ${isSelected() ? "" : "bg-tile"}`}
                        >
                          <Show when={isSelected()}>
                            <div class="w-full h-full rounded-full bg-panel scale-50" />
                          </Show>
                        </div>

                        {/* Model name with icon and description */}
                        <div class="flex items-center gap-1.5 flex-1 min-w-0">
                          <GroupIcon
                            size={14}
                            class={isSelected() ? "text-on-primary dark:text-dark-on-primary" : vendor.color}
                          />
                          <div class="flex-1 min-w-0">
                            <div class={`text-xs font-medium ${isSelected() ? "" : "text-primary-text"}`}>
                              {model.label}
                            </div>
                            <Show when={model.description}>
                              <div class={`text-xs ${isSelected() ? "opacity-90" : "text-secondary-text"}`}>
                                {model.description}
                              </div>
                            </Show>
                          </div>
                        </div>
                      </button>
                    );
                  }}
                </For>
              </>
            );
          }}
        </For>
      </div>

      <div class="flex justify-end">
        <a
          class="inline-flex items-center gap-1 underline text-brand text-xs"
          href="https://go.storytell.ai/learn-llm-router"
          target="_blank"
          rel="noreferrer"
        >
          <TbHelpCircle />
          Learn more
        </a>
      </div>
    </div>
  );
};
